// MAIA 2.0 - Build timestamp: 2025-12-23T21:10:00Z - Session persistence fix
import { Routes, Route, Navigate } from 'react-router-dom'
import { Suspense, lazy, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/auth'
import { Toaster } from '@/components/ui/toaster'
import { LoadingSpinner } from '@/components/ui/loading-spinner'

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const FioriSharePublicPage = lazy(() => import('@/pages/public/FioriSharePublicPage'))
// RegisterPage removed - registration disabled (users created by admin only)
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const MFAPage = lazy(() => import('@/pages/auth/MFAPage'))
const SelectHoldingPage = lazy(() => import('@/pages/auth/SelectHoldingPage'))
const SSOCallbackPage = lazy(() => import('@/pages/auth/SSOCallbackPage'))

const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const ChatPage = lazy(() => import('@/pages/ChatPage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const ODataDiscoveryPage = lazy(() => import('@/pages/ODataDiscoveryPage'))

// Process Mining pages
const DiscoveryPage = lazy(() => import('@/pages/pm/DiscoveryPage'))
const BrainPage = lazy(() => import('@/pages/pm/BrainPage'))
const SalDiscoveryPage = lazy(() => import('@/pages/pm/SalDiscoveryPage'))
const CasesPage = lazy(() => import('@/pages/pm/CasesPage'))
const ComparePage = lazy(() => import('@/pages/pm/ComparePage'))
const ProcessMiningPage = lazy(() => import('@/pages/ProcessMiningPage'))
const KPIsPage = lazy(() => import('@/pages/KPIsPage'))
const RulesPage = lazy(() => import('@/pages/RulesPage'))
const BPMNDesignerPage = lazy(() => import('@/pages/pm/BPMNDesignerPage'))
const BPMNDiagramsPage = lazy(() => import('@/pages/pm/BPMNDiagramsPage'))
const ConformancePage = lazy(() => import('@/pages/pm/ConformancePage'))
const SoDPage = lazy(() => import('@/pages/pm/SoDPage'))
const RCAPage = lazy(() => import('@/pages/pm/RCAPage'))
const FarosPage = lazy(() => import('@/pages/analytics/FarosPage'))
const PMConfigPage = lazy(() => import('@/pages/pm/PMConfigPage'))
const ExtractionCenterPage = lazy(() => import('@/pages/pm/ExtractionCenterPage'))
const ExtractionAuditPage = lazy(() => import('@/pages/pm/ExtractionAuditPage'))
const PMAnalysisPage = lazy(() => import('@/pages/pm/PMAnalysisPage'))
const AuditExplorerPage = lazy(() => import('@/pages/pm/AuditExplorerPage'))
const UserActivityAnalyticsPage = lazy(() => import('@/pages/pm/UserActivityAnalyticsPage'))
const VariantsPage = lazy(() => import('@/pages/pm/VariantsPage'))
const HeatmapsPage = lazy(() => import('@/pages/pm/HeatmapsPage'))
const RiskMonitoringPage = lazy(() => import('@/pages/pm/RiskMonitoringPage'))
const RelevamientoPage = lazy(() => import('@/pages/pm/RelevamientoPage'))

// Admin pages
const ChannelPartnersPage = lazy(() => import('@/pages/admin/ChannelPartnersPage'))
const HoldingsPage = lazy(() => import('@/pages/admin/HoldingsPage'))
const EmpresasPage = lazy(() => import('@/pages/admin/EmpresasPage'))
const AreasPage = lazy(() => import('@/pages/admin/AreasPage'))
const UsersPage = lazy(() => import('@/pages/admin/UsersPage'))
const UserPermissionsPage = lazy(() => import('@/pages/admin/UserPermissionsPage'))
const PromptsPage = lazy(() => import('@/pages/admin/PromptsPage'))
const RolesPermissionsPage = lazy(() => import('@/pages/admin/RolesPermissionsPage'))
const TeamsBotPage = lazy(() => import('@/pages/admin/TeamsBotPage'))
const ODataEndpointsPage = lazy(() => import('@/pages/admin/ODataEndpointsPage'))
const AIMetricsPage = lazy(() => import('@/pages/admin/AIMetricsPage'))
const MonarcaExtractionLogsPage = lazy(() => import('@/pages/admin/MonarcaExtractionLogsPage'))
const ReadinessPage = lazy(() => import('@/pages/admin/ReadinessPage'))
const ConvertPage = lazy(() => import('@/pages/admin/ConvertPage'))

// Monarca (BADM) — Migración de datos maestros
const MigrationDashboardPage = lazy(() => import('@/pages/migration/MigrationDashboardPage'))
const ProjectDetailPage = lazy(() => import('@/pages/migration/ProjectDetailPage'))
const ProjectSettingsPage = lazy(() => import('@/pages/migration/ProjectSettingsPage'))
const BusinessObjectWorkspace = lazy(() => import('@/pages/migration/BusinessObjectWorkspace'))
const CatalogPage = lazy(() => import('@/pages/migration/CatalogPage'))
const MappingsPage = lazy(() => import('@/pages/migration/MappingsPage'))
const QualityPage = lazy(() => import('@/pages/migration/QualityPage'))
const DedupPage = lazy(() => import('@/pages/migration/DedupPage'))
const DedupWorkspace = lazy(() => import('@/pages/migration/DedupWorkspace'))
const QualityWorkspace = lazy(() => import('@/pages/migration/QualityWorkspace'))
const CockpitPage = lazy(() => import('@/pages/migration/CockpitPage'))
const PipelinePage = lazy(() => import('@/pages/migration/PipelinePage'))
const StagingPage = lazy(() => import('@/pages/migration/StagingPage'))
const WorkflowPage = lazy(() => import('@/pages/migration/WorkflowPage'))
const ClustersPage = lazy(() => import('@/pages/migration/ClustersPage'))
const EnrichmentPage = lazy(() => import('@/pages/migration/EnrichmentPage'))
const AuditPage = lazy(() => import('@/pages/migration/AuditPage'))
const VerticalsMatrixPage = lazy(() => import('@/pages/admin/VerticalsMatrixPage'))

// Holding-specific pages
const HoldingPromptsPage = lazy(() => import('@/pages/holdings/HoldingPromptsPage'))

// ERP pages (Sprint 2)
const ERPConnectionsPage = lazy(() => import('@/pages/erp/ERPConnectionsPage'))

// Technical Docs (ABAP Documentation)
const TechnicalDocsPage = lazy(() => import('@/pages/techdocs/TechnicalDocsPage'))
const DocsLibraryPage = lazy(() => import('@/pages/techdocs/DocsLibraryPage'))
const IntelligentDocViewerPage = lazy(() => import('@/pages/techdocs/IntelligentDocViewerPage'))

// Fiori Builder
const FioriMigrationPage = lazy(() => import('@/pages/fiori/FioriMigrationPage'))

// Test Pages (TEMPORARY)
const TestVCT = lazy(() => import('@/pages/TestVCT'))
const TestCreateConfigs = lazy(() => import('@/pages/TestCreateConfigs'))

// Audit Analytics
const AuditAnalyticsJobsPage = lazy(() => import('@/pages/audit-analytics/AuditAnalyticsJobsPage'))
const AuditAnalyticsNewJobPage = lazy(() => import('@/pages/audit-analytics/AuditAnalyticsNewJobPage'))
const AuditAnalyticsJobPage = lazy(() => import('@/pages/audit-analytics/AuditAnalyticsJobPage'))

// Canvas/Workspace (Sprint 5)
const CanvasPage = lazy(() => import('@/pages/CanvasPage'))

// Onboarding (F5.2)
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'))

// Data Sources (Sprint 5.2)
const DataSourcesPage = lazy(() => import('@/pages/datasources/DataSourcesPage'))
const DataSourceWizardPage = lazy(() => import('@/pages/datasources/DataSourceWizardPage'))

// Business Processes (Sprint 5.3)
const BusinessProcessesPage = lazy(() => import('@/pages/business-processes/BusinessProcessesPage'))
const BusinessProcessWizardPage = lazy(() => import('@/pages/business-processes/BusinessProcessWizardPage'))
const BusinessProcessDetailPage = lazy(() => import('@/pages/business-processes/BusinessProcessDetailPage'))

// SQL Multi-DB (Sprint 5.4)
const SQLConnectionsPage = lazy(() => import('@/pages/sql/SQLConnectionsPage'))
const SQLConnectionWizardPage = lazy(() => import('@/pages/sql/SQLConnectionWizardPage'))
const SQLDataSourcesPage = lazy(() => import('@/pages/sql/SQLDataSourcesPage'))
const SQLDataSourceWizardPage = lazy(() => import('@/pages/sql/SQLDataSourceWizardPage'))
const SQLDataSourceDetailPage = lazy(() => import('@/pages/sql/SQLDataSourceDetailPage'))

// SAP Transport Analysis (Sprint 6)
const TransportOrdersPage = lazy(() => import('@/pages/transport/TransportOrdersPage'))
const TransportOrderDetailPage = lazy(() => import('@/pages/transport/TransportOrderDetailPage'))
// CTS Extraction is a tab inside ExtractionPage (PM), not a separate page

// SAP RFC/BAPI Extractors (Sprint 7)
const SAPTableExtractionPage = lazy(() => import('@/pages/sap/SAPTableExtractionPage'))
const SAPProgramWizard = lazy(() => import('@/pages/sap/SAPProgramWizard'))

// Intelligence Center
const IntelligenceCenterPage = lazy(() => import('@/pages/intelligence/IntelligenceCenterPage'))
const LicenseOptimizerPage = lazy(() => import('@/pages/govern/LicenseOptimizerPage'))

// Sandbox pages (development only)
const ProcessGraphSandbox = lazy(() => import('@/pages/ProcessGraphSandbox'))

// User Profile (Mi Conexión + RBAC)
const MyConnectionPage = lazy(() => import('@/pages/profile/MyConnectionPage'))

// Layout
import MainLayout from '@/components/layout/MainLayout'
import AuthLayout from '@/components/layout/AuthLayout'

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return <LoadingSpinner fullScreen />
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Public Route wrapper (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return <LoadingSpinner fullScreen />
  }

  if (isAuthenticated) {
    return <Navigate to="/home" replace />
  }

  return <>{children}</>
}

function App() {
  const { loadUser, user, handleSSOCompleteRedirect } = useAuthStore()
  const { i18n } = useTranslation()
  const [isInitializing, setIsInitializing] = useState(true)

  // Restore session from token on app mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // ALTA-014: Check for SSO complete redirect from centralized callback
        // This handles tokens passed via URL hash from a different subdomain
        const ssoCompleted = handleSSOCompleteRedirect()

        // Only load user from token if SSO redirect wasn't just processed
        if (!ssoCompleted) {
          await loadUser()
        }
      } finally {
        setIsInitializing(false)
      }
    }
    initAuth()
  }, [loadUser, handleSSOCompleteRedirect])

  // Sync i18n language with user's preference when user is loaded
  useEffect(() => {
    if (user?.language && user.language !== i18n.language) {
      i18n.changeLanguage(user.language)
    }
  }, [user?.language, i18n])

  // Show loading while restoring session
  if (isInitializing) {
    return <LoadingSpinner fullScreen />
  }

  return (
    <>
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            {/* Register route removed - users created by admin only */}
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />
            <Route
              path="/mfa"
              element={
                <PublicRoute>
                  <MFAPage />
                </PublicRoute>
              }
            />
            {/* INC-031: Holding selection page (not wrapped with PublicRoute - user has tempToken) */}
            <Route path="/select-holding" element={<SelectHoldingPage />} />
            {/* SSO callback - handles OAuth code exchange */}
            {/* ALTA-014: Route matches backend redirect_uri: {SSO_REDIRECT_BASE_URL}/auth/callback/{provider} */}
            <Route path="/auth/callback/:provider" element={<SSOCallbackPage />} />
          </Route>

          {/* Sandbox Routes - No auth required, for development */}
          <Route path="/sandbox/process-graph" element={<ProcessGraphSandbox />} />

          {/* INC-117: Public Fiori Builder share viewer — no auth */}
          <Route path="/shared/fiori/:token" element={<FioriSharePublicPage />} />

          {/* Protected Routes — Nav Restructure Fase 1 */}
          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            {/* ============================================= */}
            {/* HOME workspace                                */}
            {/* ============================================= */}
            <Route path="/home" element={<DashboardPage />} />
            <Route path="/home/notifications" element={<NotificationsPage />} />
            <Route path="/home/profile" element={<MyConnectionPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />

            {/* ============================================= */}
            {/* MONARCA (BADM) — Migración de datos maestros */}
            {/* ============================================= */}
            <Route path="/monarca" element={<MigrationDashboardPage />} />
            <Route path="/monarca/projects" element={<MigrationDashboardPage />} />
            <Route path="/monarca/projects/:projectId" element={<ProjectDetailPage />} />
            <Route path="/monarca/projects/:projectId/settings" element={<ProjectSettingsPage />} />
            <Route path="/monarca/projects/:projectId/objects/:boId" element={<BusinessObjectWorkspace />} />
            <Route path="/monarca/mappings" element={<MappingsPage />} />
            <Route path="/monarca/quality" element={<QualityWorkspace />} />
            <Route path="/monarca/dedup" element={<DedupWorkspace />} />
            <Route path="/monarca/cockpit" element={<CockpitPage />} />
            {/* P-MENU sec 13 audit Baco 2026-05-12 — consolidación 12 → 7:
                Workflow + Audit + Clusters + Staging dejan de ser rutas
                top-level del menú pero las URLs siguen funcionando para no
                romper bookmarks. Workflow/Audit ahora son tabs internos
                de QualityPage; Clusters es tab de DedupPage; Staging se
                accede via /monarca/projects/:id/objects/:boId tab Datos
                (los datos sueltos sin BO ya no tienen sentido en el flujo). */}
            <Route path="/monarca/workflow" element={<Navigate to="/monarca/quality?tab=workflow" replace />} />
            <Route path="/monarca/audit" element={<Navigate to="/monarca/quality?tab=history" replace />} />
            <Route path="/monarca/clusters" element={<Navigate to="/monarca/dedup?tab=clusters" replace />} />
            <Route path="/monarca/staging" element={<Navigate to="/monarca" replace />} />
            {/* Admin/Setup unificado: Catálogo + Pipeline + Enrichment +
                Conexiones SAP. Las rutas legacy redirigen a la nueva
                sección con sub-tab; la nueva /monarca/admin lista todas. */}
            <Route path="/monarca/admin" element={<CatalogPage />} />
            <Route path="/monarca/catalog" element={<Navigate to="/monarca/admin?tab=catalog" replace />} />
            <Route path="/monarca/pipeline" element={<Navigate to="/monarca/admin?tab=pipeline" replace />} />
            <Route path="/monarca/enrichment" element={<Navigate to="/monarca/admin?tab=enrichment" replace />} />

            {/* ============================================= */}
            {/* EXTRACT workspace (pipeline step 1)           */}
            {/* ============================================= */}
            <Route path="/analytics/faros" element={<FarosPage />} />
            <Route path="/extract/erp" element={<ERPConnectionsPage />} />
            <Route path="/extract/sql" element={<SQLConnectionsPage />} />
            <Route path="/extract/sql/new" element={<SQLConnectionWizardPage />} />
            <Route path="/extract/sql/:id/edit" element={<SQLConnectionWizardPage />} />
            <Route path="/extract/sources" element={<DataSourcesPage />} />
            <Route path="/extract/sources/new" element={<DataSourceWizardPage />} />
            <Route path="/extract/sources/:id" element={<DataSourceWizardPage />} />
            <Route path="/extract/sources/:id/edit" element={<DataSourceWizardPage />} />
            <Route path="/extract/sap" element={<SAPProgramWizard />} />
            <Route path="/extract/sap/:configId" element={<SAPProgramWizard />} />
            <Route path="/extract/sap-tables" element={<SAPTableExtractionPage />} />
            <Route path="/extract/sap-tables/:configId" element={<SAPTableExtractionPage />} />
            <Route path="/extract/odata" element={<ODataDiscoveryPage />} />
            <Route path="/extract/center" element={<ExtractionCenterPage />} />
            <Route path="/extract/jobs" element={<Navigate to="/extract/center" replace />} />
            <Route path="/extract/endpoints" element={<ODataEndpointsPage />} />
            <Route path="/extract/sql-datasources" element={<SQLDataSourcesPage />} />
            <Route path="/extract/sql-datasources/new" element={<SQLDataSourceWizardPage />} />
            <Route path="/extract/sql-datasources/:id" element={<SQLDataSourceDetailPage />} />
            <Route path="/extract/sql-datasources/:id/edit" element={<SQLDataSourceWizardPage />} />

            {/* ============================================= */}
            {/* RECONSTRUCT workspace (pipeline step 2)       */}
            {/* ============================================= */}
            <Route path="/reconstruct" element={<Navigate to="/reconstruct/discovery" replace />} />
            <Route path="/reconstruct/discovery" element={<DiscoveryPage />} />
            <Route path="/reconstruct/sal-discovery" element={<SalDiscoveryPage />} />
            <Route path="/reconstruct/cases" element={<CasesPage />} />
            <Route path="/reconstruct/compare" element={<ComparePage />} />
            <Route path="/reconstruct/discovery-legacy" element={<ProcessMiningPage />} />
            <Route path="/reconstruct/variants" element={<VariantsPage />} />
            <Route path="/reconstruct/kpis" element={<KPIsPage />} />
            <Route path="/reconstruct/conformance" element={<ConformancePage />} />
            <Route path="/reconstruct/heatmaps" element={<HeatmapsPage />} />
            <Route path="/reconstruct/users" element={<UserActivityAnalyticsPage />} />
            <Route path="/reconstruct/analysis" element={<PMAnalysisPage />} />
            <Route path="/reconstruct/relevamiento" element={<RelevamientoPage />} />
            <Route path="/reconstruct/config" element={<PMConfigPage />} />
            <Route path="/reconstruct/audit-explorer" element={<AuditExplorerPage />} />
            <Route path="/reconstruct/rules" element={<RulesPage />} />
            {/* Hidden admin page */}
            <Route path="/reconstruct/extraction-audit" element={<ExtractionAuditPage />} />

            {/* ============================================= */}
            {/* DESIGN workspace (pipeline step 3)            */}
            {/* ============================================= */}
            <Route path="/design" element={<Navigate to="/design/processes" replace />} />
            <Route path="/design/processes" element={<BusinessProcessesPage />} />
            <Route path="/design/processes/new" element={<BusinessProcessWizardPage />} />
            <Route path="/design/processes/:id" element={<BusinessProcessDetailPage />} />
            <Route path="/design/processes/:id/edit" element={<BusinessProcessWizardPage />} />
            <Route path="/design/processes/:id/execute" element={<BusinessProcessDetailPage />} />
            <Route path="/design/bpmn" element={<BPMNDesignerPage />} />
            <Route path="/design/bpmn/:id" element={<BPMNDesignerPage />} />
            <Route path="/design/diagrams" element={<BPMNDiagramsPage />} />
            <Route path="/design/fiori" element={<FioriMigrationPage />} />
            <Route path="/design/docs" element={<TechnicalDocsPage />} />
            <Route path="/design/library" element={<DocsLibraryPage />} />
            <Route path="/design/library/intelligent-docs/:docId"
                   element={<IntelligentDocViewerPage />} />
            <Route path="/design/transport" element={<TransportOrdersPage />} />
            <Route path="/design/transport/:orderId" element={<TransportOrderDetailPage />} />

            {/* ============================================= */}
            {/* AUDIT ANALYTICS (from Design → Library)       */}
            {/* ============================================= */}
            <Route path="/audit-analytics/jobs" element={<AuditAnalyticsJobsPage />} />
            <Route path="/audit-analytics/jobs/new" element={<AuditAnalyticsNewJobPage />} />
            <Route path="/audit-analytics/jobs/:jobId" element={<AuditAnalyticsJobPage />} />

            {/* ============================================= */}
            {/* GOVERN workspace (pipeline step 4)            */}
            {/* ============================================= */}
            <Route path="/govern" element={<Navigate to="/govern/dashboard" replace />} />
            {/* Standalone pages (must be before :section catch-all) */}
            <Route path="/govern/rca" element={<RCAPage />} />
            <Route path="/govern/monitoring" element={<RiskMonitoringPage />} />
            <Route path="/govern/sod-standalone" element={<SoDPage />} />
            <Route path="/govern/license-optimizer" element={<LicenseOptimizerPage />} />
            {/* Intelligence Center sections */}
            <Route path="/govern/:section" element={<IntelligenceCenterPage />} />

            {/* ============================================= */}
            {/* AI workspace                                  */}
            {/* ============================================= */}
            <Route path="/ai/razonamiento" element={<BrainPage />} />
            <Route path="/ai/chat" element={<ChatPage />} />
            <Route path="/ai/canvas" element={<CanvasPage />} />

            {/* ============================================= */}
            {/* ADMIN workspace                               */}
            {/* ============================================= */}
            <Route path="/admin/channel-partners" element={<ChannelPartnersPage />} />
            <Route path="/admin/holdings" element={<HoldingsPage />} />
            <Route path="/admin/holdings/:holdingId/prompts" element={<HoldingPromptsPage />} />
            <Route path="/admin/empresas" element={<EmpresasPage />} />
            <Route path="/admin/areas" element={<AreasPage />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/admin/users/:userId/permissions" element={<UserPermissionsPage />} />
            <Route path="/admin/prompts" element={<PromptsPage />} />
            <Route path="/admin/rbac" element={<RolesPermissionsPage />} />
            <Route path="/admin/teams-bot" element={<TeamsBotPage />} />
            <Route path="/admin/ai-metrics" element={<AIMetricsPage />} />
            <Route path="/admin/monarca-logs" element={<MonarcaExtractionLogsPage />} />
            <Route path="/admin/readiness" element={<ReadinessPage />} />
            <Route path="/admin/convert" element={<ConvertPage />} />
            <Route path="/admin/verticals" element={<VerticalsMatrixPage />} />

            {/* ============================================= */}
            {/* BACKWARD COMPATIBILITY REDIRECTS — Fase 1     */}
            {/* Keep for 3 months, remove in Fase 3           */}
            {/* ============================================= */}

            {/* Test routes - TEMPORARY */}
            <Route path="/test-vct" element={<TestVCT />} />
            <Route path="/test-configs" element={<TestCreateConfigs />} />

            {/* /dashboard, /chat, etc → home/ai */}
            <Route path="/dashboard" element={<Navigate to="/home" replace />} />
            <Route path="/chat" element={<Navigate to="/ai/chat" replace />} />
            <Route path="/notifications" element={<Navigate to="/home/notifications" replace />} />
            <Route path="/profile" element={<Navigate to="/home/profile" replace />} />
            <Route path="/canvas" element={<Navigate to="/ai/canvas" replace />} />
            <Route path="/odata-discovery" element={<Navigate to="/extract/odata" replace />} />

            {/* /erp/*, /sql/*, /datasources/* → /extract/* */}
            <Route path="/erp/connections" element={<Navigate to="/extract/erp" replace />} />
            <Route path="/sql/connections" element={<Navigate to="/extract/sql" replace />} />
            <Route path="/sql/connections/new" element={<Navigate to="/extract/sql/new" replace />} />
            <Route path="/sql/datasources" element={<Navigate to="/extract/sql-datasources" replace />} />
            <Route path="/datasources" element={<Navigate to="/extract/sources" replace />} />
            <Route path="/admin/odata-endpoints" element={<Navigate to="/extract/endpoints" replace />} />

            {/* /sap/* → /extract/sap* */}
            <Route path="/sap/extractors" element={<Navigate to="/extract/sap" replace />} />
            <Route path="/sap/extractors/transaction/new" element={<Navigate to="/extract/sap" replace />} />
            <Route path="/sap/extractors/table/new" element={<Navigate to="/extract/sap-tables" replace />} />

            {/* /pm/* → /reconstruct/*, /extract/*, /design/*, /govern/* */}
            <Route path="/pm/discovery" element={<Navigate to="/reconstruct/discovery" replace />} />
            <Route path="/pm/variants" element={<Navigate to="/reconstruct/variants" replace />} />
            <Route path="/pm/kpis" element={<Navigate to="/reconstruct/kpis" replace />} />
            <Route path="/pm/conformance" element={<Navigate to="/reconstruct/conformance" replace />} />
            <Route path="/pm/heatmaps" element={<Navigate to="/reconstruct/heatmaps" replace />} />
            <Route path="/pm/user-activity" element={<Navigate to="/reconstruct/users" replace />} />
            <Route path="/pm/analysis" element={<Navigate to="/reconstruct/analysis" replace />} />
            <Route path="/pm/config" element={<Navigate to="/reconstruct/config" replace />} />
            <Route path="/pm/audit-explorer" element={<Navigate to="/reconstruct/audit-explorer" replace />} />
            <Route path="/pm/extraction" element={<Navigate to="/extract/center" replace />} />
            <Route path="/pm/extraction-audit" element={<Navigate to="/reconstruct/extraction-audit" replace />} />
            <Route path="/pm/designer" element={<Navigate to="/design/bpmn" replace />} />
            <Route path="/pm/diagrams" element={<Navigate to="/design/diagrams" replace />} />
            <Route path="/pm/sod" element={<Navigate to="/govern/sod" replace />} />
            <Route path="/pm/rca" element={<Navigate to="/govern/rca" replace />} />
            <Route path="/pm/monitoring" element={<Navigate to="/govern/monitoring" replace />} />
            <Route path="/pm/rules" element={<Navigate to="/reconstruct/rules" replace />} />
            <Route path="/pm/discovery-legacy" element={<Navigate to="/reconstruct/discovery-legacy" replace />} />
            <Route path="/process-mining" element={<Navigate to="/reconstruct/discovery" replace />} />
            <Route path="/kpis" element={<Navigate to="/reconstruct/kpis" replace />} />
            <Route path="/rules" element={<Navigate to="/reconstruct/rules" replace />} />

            {/* /business-processes/* → /design/processes/* */}
            <Route path="/business-processes" element={<Navigate to="/design/processes" replace />} />

            {/* /techdocs, /docs-library, /fiori-migration, /transport/* → /design/* */}
            <Route path="/techdocs" element={<Navigate to="/design/docs" replace />} />
            <Route path="/docs-library" element={<Navigate to="/design/library" replace />} />
            <Route path="/fiori-migration" element={<Navigate to="/design/fiori" replace />} />
            <Route path="/transport/orders" element={<Navigate to="/design/transport" replace />} />
            <Route path="/transport/extract" element={<Navigate to="/design/transport" replace />} />

            {/* /intelligence/* → /govern/* */}
            <Route path="/intelligence" element={<Navigate to="/govern/dashboard" replace />} />
            <Route path="/intelligence/:section" element={<IntelligenceCenterPage />} />
          </Route>

          {/* Root redirects */}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
    </>
  )
}

export default App
